import { Component, OnInit } from '@angular/core';
import { AttendanceService } from '../../services/attendance.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as XLSX from 'xlsx';

interface MonthlyReportData {
  monthYear: string;
  employeeDailyHours: EmployeeDailyHours[];
  dailyTotals: DailyTotal[];
  employeeMonthlyTotals: EmployeeMonthlyTotal[];
  grandTotalHours: number;
}

interface EmployeeDailyHours {
  employeeId: number;
  employeeName: string;
  dailyHours: DailyHours[];
  monthlyTotal: number;
  hourlyRate: number;
  monthlySalary: number;
  salaryType: number; // 1 = Hourly, 2 = Monthly
}

interface DailyHours {
  date: string;
  hours: number;
  isIncompleteShift?: boolean;
}

interface DailyTotal {
  date: string;
  totalHours: number;
}

interface EmployeeMonthlyTotal {
  employeeId: number;
  employeeName: string;
  totalHours: number;
}

interface SalaryCalculation {
  employeeId: number;
  employeeName: string;
  salaryType: number; // 1 = Hourly, 2 = Monthly
  hourlyRate: number;
  monthlySalary: number;
  totalHours: number;
  standardHours: number;
  overtimeHours: number;
  standardPay: number;
  overtimePay: number;
  grossPay: number;
}

@Component({
  selector: 'app-monthly-attendance-report',
  templateUrl: './monthly-attendance-report.component.html',
  styleUrls: ['./monthly-attendance-report.component.scss']
})
export class MonthlyAttendanceReportComponent implements OnInit {
  loading = false;
  reportData: MonthlyReportData | null = null;
  selectedYear: number = new Date().getFullYear();
  selectedMonth: number = new Date().getMonth() + 1;
  
  // Salary calculation settings
  standardHours: number = 80;
  overtimeRate: number = 9;
  salaryCalculations: SalaryCalculation[] = [];
  
  // Month options
  months = [
    { value: 1, name: 'January' },
    { value: 2, name: 'February' },
    { value: 3, name: 'March' },
    { value: 4, name: 'April' },
    { value: 5, name: 'May' },
    { value: 6, name: 'June' },
    { value: 7, name: 'July' },
    { value: 8, name: 'August' },
    { value: 9, name: 'September' },
    { value: 10, name: 'October' },
    { value: 11, name: 'November' },
    { value: 12, name: 'December' }
  ];

  // Year options (current year ± 5 years)
  years: number[] = [];

  constructor(
    private attendanceService: AttendanceService,
    private snackBar: MatSnackBar
  ) {
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      this.years.push(i);
    }
  }

  ngOnInit(): void {
    this.loadReport();
  }

  onMonthYearChange(): void {
    this.loadReport();
  }

  loadReport(): void {
    this.loading = true;
    this.reportData = null;

    this.attendanceService.getMonthlyAttendanceReport(this.selectedYear, this.selectedMonth).subscribe({
      next: (data) => {
        this.reportData = data;
        this.calculateSalaries();
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.open(`Error loading report: ${error.error?.message || error.message}`, 'Close', { duration: 5000 });
      }
    });
  }

  exportToExcel(): void {
    if (!this.reportData) {
      this.snackBar.open('No data to export', 'Close', { duration: 3000 });
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();
      const worksheetData = this.createExcelWorksheet();
      
      if (worksheetData.length === 0) {
        this.snackBar.open('No data to export', 'Close', { duration: 3000 });
        return;
      }
      
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      
      // Set column widths
      const colWidths = [
        { wch: 12 }, // Date column
        ...this.reportData.employeeDailyHours.map(() => ({ wch: 10 })), // Employee columns
        { wch: 12 } // Total column
      ];
      worksheet['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Monthly Report');
      
      // Generate filename
      const monthName = this.months.find(m => m.value === this.selectedMonth)?.name || 'Unknown';
      const filename = `${monthName}_${this.selectedYear}_Attendance_Report.xlsx`;
      
      XLSX.writeFile(workbook, filename);
      this.snackBar.open('Report exported successfully', 'Close', { duration: 3000 });
    } catch (error) {
      console.error('Excel export error:', error);
      this.snackBar.open(`Error exporting report: ${error.message}`, 'Close', { duration: 5000 });
    }
  }

  private createExcelWorksheet(): any[][] {
    if (!this.reportData) {
      console.log('No report data available');
      return [];
    }

    console.log('Creating Excel worksheet with report data:', this.reportData);
    
    const data: any[][] = [];
    
    // Header row: Month/Year, employee names, and Total Hours
    const headerRow = [this.reportData.monthYear];
    // Add employee names
    this.reportData.employeeDailyHours.forEach(emp => {
      headerRow.push(emp.employeeName);
    });
    headerRow.push('Total Hours');
    data.push(headerRow);
    console.log('Header row:', headerRow);

    // Data rows: Each day with hours for each employee
    const daysInMonth = this.getDaysInMonth(this.selectedYear, this.selectedMonth);
    console.log('Days in month:', daysInMonth.length);
    
    daysInMonth.forEach(day => {
      const row = [this.formatDate(day)];
      
      // Add hours for each employee for this day
      this.reportData!.employeeDailyHours.forEach(emp => {
        const dayHours = emp.dailyHours.find(dh => dh.date === day.toISOString().split('T')[0]);
        const hoursValue = dayHours && dayHours.hours > 0 ? dayHours.hours.toString() : '';
        row.push(hoursValue);
      });
      
      // Add daily total
      const dailyTotal = this.reportData!.dailyTotals.find(dt => dt.date === day.toISOString().split('T')[0]);
      const totalValue = dailyTotal && dailyTotal.totalHours > 0 ? dailyTotal.totalHours.toString() : '';
      row.push(totalValue);
      
      data.push(row);
    });

    // Total row
    const totalRow = ['Total Hours'];
    this.reportData.employeeMonthlyTotals.forEach(emp => {
      totalRow.push(emp.totalHours.toString());
    });
    totalRow.push(this.reportData.grandTotalHours.toString());
    data.push(totalRow);

    return data;
  }

  private getDaysInMonth(year: number, month: number): Date[] {
    const daysInMonth = new Date(year, month, 0).getDate();
    const days: Date[] = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      // Create date in UTC to avoid timezone issues
      const date = new Date(Date.UTC(year, month - 1, day));
      days.push(date);
    }
    
    return days;
  }

  private formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    return `${day}-${month}`;
  }

  getDaysInMonthArray(): Date[] {
    return this.getDaysInMonth(this.selectedYear, this.selectedMonth);
  }

  getEmployeeHoursForDay(employee: EmployeeDailyHours, day: Date): number {
    const dateStr = day.toISOString().split('T')[0];
    const dayHours = employee.dailyHours.find(dh => dh.date === dateStr);
    return dayHours ? dayHours.hours : 0;
  }

  isIncompleteShift(employee: EmployeeDailyHours, day: Date): boolean {
    const dateStr = day.toISOString().split('T')[0];
    const dayHours = employee.dailyHours.find(dh => dh.date === dateStr);
    return dayHours ? dayHours.isIncompleteShift || false : false;
  }

  getDailyTotalForDay(day: Date): number {
    const dateStr = day.toISOString().split('T')[0];
    const dailyTotal = this.reportData?.dailyTotals.find(dt => dt.date === dateStr);
    return dailyTotal ? dailyTotal.totalHours : 0;
  }

  onSalarySettingsChange(): void {
    this.calculateSalaries();
  }

  calculateSalaries(): void {
    if (!this.reportData) return;

    this.salaryCalculations = [];

    this.reportData.employeeDailyHours.forEach(emp => {
      const totalHours = emp.monthlyTotal;
      
      const salaryCalc: SalaryCalculation = {
        employeeId: emp.employeeId,
        employeeName: emp.employeeName,
        salaryType: emp.salaryType,
        hourlyRate: emp.hourlyRate,
        monthlySalary: emp.monthlySalary,
        totalHours: totalHours,
        standardHours: this.standardHours,
        overtimeHours: Math.max(0, totalHours - this.standardHours),
        standardPay: 0,
        overtimePay: 0,
        grossPay: 0
      };

      // Calculate based on salary type
      if (salaryCalc.salaryType === 2) {
        // Monthly salary - just use the monthly salary amount
        salaryCalc.grossPay = salaryCalc.monthlySalary;
      } else {
        // Hourly calculation
        salaryCalc.standardPay = Math.min(totalHours, this.standardHours) * salaryCalc.hourlyRate;
        salaryCalc.overtimePay = salaryCalc.overtimeHours * this.overtimeRate;
        salaryCalc.grossPay = salaryCalc.standardPay + salaryCalc.overtimePay;
      }

      this.salaryCalculations.push(salaryCalc);
    });

    console.log('Salary calculations:', this.salaryCalculations);
  }

  getTotalHours(): number {
    return this.salaryCalculations.reduce((sum, calc) => sum + calc.totalHours, 0);
  }

  getTotalOvertimeHours(): number {
    return this.salaryCalculations.reduce((sum, calc) => sum + calc.overtimeHours, 0);
  }

  getTotalStandardPay(): number {
    return this.salaryCalculations.reduce((sum, calc) => sum + calc.standardPay, 0);
  }

  getTotalOvertimePay(): number {
    return this.salaryCalculations.reduce((sum, calc) => sum + calc.overtimePay, 0);
  }

  getTotalGrossPay(): number {
    return this.salaryCalculations.reduce((sum, calc) => sum + calc.grossPay, 0);
  }
}

